import {
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Sort, MatSortModule } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { TableHeadersModalComponent } from './table-headers-modal.component';
import { ExcelExportService } from '../../services/excel-export.service';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';

type TableHeader = { value: string; description: string };
type HeaderFixedPosition = 'start' | 'end' | 'both' | null;
type TableHeaderConfig = TableHeader & {
  order: number;
  visible: boolean;
  fixedPosition: HeaderFixedPosition;
};

@Component({
  selector: 'app-table-general-simple',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSortModule,
    MatTooltip,
    TranslateModule,
    MatPaginatorModule,
  ],
  templateUrl: './table-general-simple.component.html',
  styleUrls: ['./table-general-simple.component.scss'],
})
export class TableGeneralSimpleComponent implements OnInit, OnChanges, OnDestroy {
  // Query reactivo: emite en tiempo real en cuanto el componente existe en el DOM
  public readonly paginatorComponent = viewChild<MatPaginator>('MatPaginatorGeneral');

  @Output() idEmitter = new EventEmitter<number>();
  @Output() elementEmitter = new EventEmitter<any>();
  @Output() sortTable = new EventEmitter<Sort>();
  @Output() endElementEmitter = new EventEmitter<{ key: string; element: any }>();
  @Output() expandRow = new EventEmitter<any>();

  private _headers: TableHeader[] = [];
  public readonly headerOptions = signal<TableHeaderConfig[]>([]);
  public visibleHeaders: TableHeader[] = [];
  public headerKeys: string[] = [];

  @Input() set headers(value: TableHeader[]) {
    this._headers = value ?? [];
    this.syncHeaderOptions(this._headers);
  }

  get headers(): TableHeader[] {
    return this._headers;
  }

  // Inputs reactivos basados en Signals
  public readonly dataSource = input<MatTableDataSource<any>>(new MatTableDataSource<any>([]));
  public readonly paginator = input<boolean>(false);
  public readonly pageSize = input<number>(10);

  @Input() subHeaders: { value: string; description: string }[] = [];
  @Input() isLoading = false;
  @Input() buttonEvent = true;
  @Input() checkEvent = false;
  @Input() isSticky = false;
  @Input() isStickyEnd = false;
  @Input() headerSorts: string[] = [];
  @Input() menuButton: {
    icon?: string;
    iconFile?: string;
    key: string;
    text: string;
    toolTip?: string;
  }[] = [];
  @Input() featuredRows: number[] = [];
  @Input() totalAmount: number | null = null;
  @Input() hasExpansion = false;
  @Input() excelEvent = false;
  @Input() excelFileName = 'reporte';
  @Input() excelData: any[] | null = null;

  public isMobile = false;
  public expandedElement: any | null = null;

  constructor(
    public dialog: MatDialog,
    private excelExportService: ExcelExportService,
  ) {
    // El effect rastrea dataSource, paginator y paginatorComponent
    // Se ejecuta sin condiciones previas que bloqueen el contexto de inyección
    effect(() => {
      const currentDs = this.dataSource();
      const isPaginatorEnabled = this.paginator();
      const currentPaginator = this.paginatorComponent();

      if (!currentDs) return;

      if (isPaginatorEnabled && currentPaginator) {
        currentDs.paginator = currentPaginator;
      } else if (!isPaginatorEnabled) {
        currentDs.paginator = null;
      }
    });
  }

  ngOnInit(): void {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile.bind(this));
  }

  ngOnChanges(): void {
    if (this._headers.length > 0) {
      this.syncHeaderOptions(this._headers);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.checkMobile.bind(this));
  }

  private checkMobile(): void {
    this.isMobile = window.matchMedia('(max-width: 768px)').matches;
  }

  public emitterButton(id: number, element: any): void {
    if (this.hasExpansion) {
      this.expandRow.emit(element);
    } else {
      this.idEmitter.emit(id);
      this.elementEmitter.emit(element);
    }
  }

  public endEmitter(key: string, element: any): void {
    this.endElementEmitter.emit({ key, element });
  }

  public updateElement(item: any, header: string, event: boolean) {
    item[header] = event;
  }

  public isBoolean(value: unknown): boolean {
    return typeof value === 'boolean';
  }

  public downloadExcel(): void {
    this.excelExportService.exportToExcel(
      this.visibleHeaders,
      this.excelData ?? this.dataSource().filteredData,
      this.excelFileName,
    );
  }

  public openHeadersModal(): void {
    const dialogRef = this.dialog.open(TableHeadersModalComponent, {
      width: '680px',
      maxWidth: '95vw',
      data: {
        headers: this.headerOptions(),
      },
    });

    dialogRef.afterClosed().subscribe((headers: TableHeaderConfig[] | null) => {
      if (!headers) return;

      this.headerOptions.set(this.normalizeHeaderOptions(headers));
      this.refreshRenderedHeaders();
    });
  }

  private syncHeaderOptions(headers: TableHeader[]): void {
    const currentHeaders = new Map(this.headerOptions().map((header) => [header.value, header]));
    const fixedStartValue = this.shouldLockFirstHeader() ? headers[0]?.value : undefined;
    const fixedEndValue = this.shouldLockLastHeader()
      ? headers[headers.length - 1]?.value
      : undefined;

    const nextHeaders = headers.map((header, index) => {
      const currentHeader = currentHeaders.get(header.value);
      const fixedPosition = this.resolveFixedPosition(header.value, fixedStartValue, fixedEndValue);

      return {
        value: header.value,
        description: header.description,
        visible: fixedPosition ? true : (currentHeader?.visible ?? true),
        order: currentHeader?.order ?? index + 1,
        fixedPosition,
      };
    });

    this.headerOptions.set(this.normalizeHeaderOptions(nextHeaders));
    this.refreshRenderedHeaders();
  }

  private normalizeHeaderOptions(headers: TableHeaderConfig[]): TableHeaderConfig[] {
    const startHeaders: TableHeaderConfig[] = [];
    const middleHeaders: TableHeaderConfig[] = [];
    const endHeaders: TableHeaderConfig[] = [];

    headers
      .slice()
      .sort((left, right) => left.order - right.order)
      .forEach((header) => {
        if (header.fixedPosition === 'start' || header.fixedPosition === 'both') {
          startHeaders.push(header);
          return;
        }

        if (header.fixedPosition === 'end') {
          endHeaders.push(header);
          return;
        }

        middleHeaders.push(header);
      });

    return [...startHeaders, ...middleHeaders, ...endHeaders].map((header, index) => ({
      ...header,
      visible: header.fixedPosition ? true : header.visible,
      order: index + 1,
    }));
  }

  private shouldLockFirstHeader(): boolean {
    return (
      (!this.hasExpansion && this.buttonEvent && !this.checkEvent) ||
      (!this.hasExpansion && !this.buttonEvent && this.checkEvent) ||
      (this.hasExpansion && !this.buttonEvent && !this.checkEvent)
    );
  }

  private shouldLockLastHeader(): boolean {
    return this.menuButton.length > 0;
  }

  private resolveFixedPosition(
    value: string,
    fixedStartValue?: string,
    fixedEndValue?: string,
  ): HeaderFixedPosition {
    if (fixedStartValue && fixedEndValue && fixedStartValue === value && fixedEndValue === value) {
      return 'both';
    }

    if (fixedStartValue === value) {
      return 'start';
    }

    if (fixedEndValue === value) {
      return 'end';
    }

    return null;
  }

  private refreshRenderedHeaders(): void {
    this.visibleHeaders = this.headerOptions()
      .filter((header) => header.visible)
      .sort((left, right) => left.order - right.order)
      .map(({ value, description }) => ({ value, description }));

    this.headerKeys = this.visibleHeaders.map((header) => header.value);
  }

  public announceSortChange(sortState: Sort) {
    this.sortTable.emit(sortState);
  }

  public existsInSort(search: string): boolean {
    return this.headerSorts.includes(search);
  }

  public rowSearch(rowNumber: number): boolean {
    return this.featuredRows.includes(rowNumber);
  }

  public toggleRow(element: any): void {
    if (this.expandedElement === element) {
      this.expandedElement = null;
    } else {
      this.expandedElement = element;
      this.expandRow.emit(element);
    }
  }
}
